import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Building2, 
  ChevronDown, 
  Check, 
  Settings, 
  Sparkles,
  ExternalLink,
  ShieldCheck,
  Briefcase,
  Trash2,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { useAuth, VendorProfile } from '../AuthContext';
import { motion, AnimatePresence } from 'motion/react';

interface VendorProfileSwitcherProps {
  variant?: 'sidebar' | 'header' | 'compact' | 'collapsed';
  onOpenCreateModal?: () => void;
  onNavigateToProfiles?: () => void;
}

export const VendorProfileSwitcher: React.FC<VendorProfileSwitcherProps> = ({
  variant = 'sidebar',
  onOpenCreateModal,
  onNavigateToProfiles
}) => {
  const { vendorProfile, vendorProfiles, activeProfileId, switchProfile, deleteProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<VendorProfile | null>(null);
  const [isDeletingEntity, setIsDeletingEntity] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProfile = async (id: string) => {
    if (id !== activeProfileId) {
      await switchProfile(id);
    }
    setIsOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!entityToDelete?.id) return;
    setIsDeletingEntity(true);
    setDeleteError(null);
    try {
      await deleteProfile(entityToDelete.id);
      setEntityToDelete(null);
    } catch (err: any) {
      console.error("Failed to delete business entity:", err);
      setDeleteError(err.message || 'Failed to delete business entity');
    } finally {
      setIsDeletingEntity(false);
    }
  };

  const activeName = vendorProfile?.companyName || 'Select Vendor Profile';
  const activeLabel = vendorProfile?.label || 'Primary Entity';
  const profileCount = vendorProfiles.length;

  const deleteModal = entityToDelete && typeof document !== 'undefined' ? createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white max-w-md w-full rounded-2xl p-6 shadow-2xl border border-slate-200 space-y-4 animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-start gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100">
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-bold text-slate-900">Delete Business Entity?</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Are you sure you want to delete this business entity from your vendor profiles?
            </p>
          </div>
        </div>

        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-900 text-sm truncate">{entityToDelete.companyName || 'Untitled Entity'}</span>
            <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-indigo-50 text-indigo-700 border border-indigo-200">
              {entityToDelete.label || 'Entity'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
            <div><span className="font-semibold text-slate-400">EIN / Tax ID:</span> {entityToDelete.ein || 'None'}</div>
            <div><span className="font-semibold text-slate-400">Jurisdiction:</span> {entityToDelete.businessRegisteredState || 'US'}</div>
            <div><span className="font-semibold text-slate-400">Company ID:</span> {entityToDelete.companyId || entityToDelete.dosId || 'None'}</div>
            <div><span className="font-semibold text-slate-400">NAICS:</span> {entityToDelete.naicsCodes?.length || 0} Tracked</div>
          </div>
          {vendorProfiles.length === 1 && (
            <p className="text-[11px] text-amber-700 font-medium bg-amber-50 p-2 rounded border border-amber-200">
              ⚠️ This is your only active business entity. Deleting it will clear and reset your vendor profile to an initial state.
            </p>
          )}
        </div>

        {deleteError && (
          <p className="text-xs text-rose-600 font-medium bg-rose-50 p-2.5 rounded-lg border border-rose-200">
            {deleteError}
          </p>
        )}

        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-slate-100">
          <button
            type="button"
            disabled={isDeletingEntity}
            onClick={() => {
              setEntityToDelete(null);
              setDeleteError(null);
            }}
            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isDeletingEntity}
            onClick={handleDeleteConfirm}
            className="px-4 py-2 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 rounded-xl transition-colors cursor-pointer shadow-sm flex items-center gap-1.5"
          >
            {isDeletingEntity ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-3.5 h-3.5" />
                <span>Confirm Delete</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (variant === 'collapsed') {
    return (
      <div className="relative flex justify-center" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          id="btn-vendor-switcher-collapsed"
          title={`Entity: ${activeName} (${profileCount} profiles)`}
          className="w-10 h-10 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 flex items-center justify-center transition-all cursor-pointer shadow-xs relative"
        >
          <Building2 className="w-5 h-5" />
          {profileCount > 1 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-600 text-white rounded-full text-[9px] font-extrabold flex items-center justify-center">
              {profileCount}
            </span>
          )}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, x: 10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.95, x: 10 }}
              transition={{ duration: 0.15 }}
              className="absolute left-12 top-0 w-64 bg-white rounded-2xl border border-slate-200 shadow-xl py-2 z-50 overflow-hidden"
            >
              <div className="px-3.5 py-2 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Switch Active Entity</p>
                <p className="text-xs text-slate-600 font-medium">{profileCount} {profileCount === 1 ? 'Profile' : 'Profiles'} Configured</p>
              </div>

              <div className="max-h-72 overflow-y-auto py-1 divide-y divide-slate-50">
                {vendorProfiles.map((p) => {
                  const isSelected = p.id === activeProfileId;
                  return (
                    <div
                      key={p.id}
                      onClick={() => handleSelectProfile(p.id!)}
                      className={`p-3 hover:bg-slate-50 transition-colors cursor-pointer flex items-start justify-between gap-2 group/row ${
                        isSelected ? 'bg-indigo-50/40' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[9px] font-black uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                            {p.label || 'Entity'}
                          </span>
                          {p.businessRegisteredState && (
                            <span className="text-[9px] font-mono font-bold text-slate-500">
                              {p.businessRegisteredState}
                            </span>
                          )}
                        </div>
                        <p className="font-bold text-xs text-slate-900 truncate mt-1">
                          {p.companyName}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0 mt-0.5">
                        <button
                          type="button"
                          title="Delete business entity"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEntityToDelete(p);
                          }}
                          className="p-1 hover:bg-rose-100 text-slate-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {onNavigateToProfiles && (
                <div className="p-2 border-t border-slate-100 bg-slate-50/50">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onNavigateToProfiles();
                    }}
                    className="w-full py-1.5 px-3 text-xs font-bold text-slate-700 hover:text-indigo-600 hover:bg-white border border-slate-200 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Settings className="w-3.5 h-3.5" /> Manage Entity Registry
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {deleteModal}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          id="btn-vendor-switcher-compact"
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 shadow-xs transition-all cursor-pointer"
        >
          <Building2 className="w-3.5 h-3.5 text-indigo-600" />
          <span className="max-w-[140px] truncate">{activeName}</span>
          <span className="text-[10px] px-1.5 py-0.2 bg-indigo-50 text-indigo-700 rounded font-bold">{profileCount}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl py-2 z-50 overflow-hidden"
            >
              <div className="px-3.5 py-2 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Switch Active Entity</p>
                <p className="text-xs text-slate-600 font-medium">{profileCount} {profileCount === 1 ? 'Profile' : 'Profiles'} Configured</p>
              </div>

              <div className="max-h-60 overflow-y-auto py-1">
                {vendorProfiles.map((p) => {
                  const isSelected = p.id === activeProfileId;
                  return (
                    <div
                      key={p.id}
                      onClick={() => p.id && handleSelectProfile(p.id)}
                      className={`px-3.5 py-2.5 flex items-center justify-between hover:bg-slate-50 cursor-pointer transition-colors group/item ${
                        isSelected ? 'bg-indigo-50/70 border-l-3 border-indigo-600' : ''
                      }`}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-xs font-bold truncate ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                            {p.companyName || 'Untitled Entity'}
                          </p>
                          {p.isDefault && (
                            <span className="text-[9px] px-1 bg-slate-100 text-slate-600 rounded font-semibold">Default</span>
                          )}
                          {p.businessEntityData && (
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" title="Corporate Registry Verified" />
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                          {p.label || 'Entity Profile'} • {p.businessRegisteredState || 'US'} {p.ein ? `• EIN: ${p.ein}` : ''}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          title="Delete business entity"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEntityToDelete(p);
                          }}
                          className="p-1 hover:bg-rose-100 text-slate-400 hover:text-rose-600 rounded transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {isSelected && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {onNavigateToProfiles && (
                <div className="p-2 border-t border-slate-100 bg-slate-50/50">
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      onNavigateToProfiles();
                    }}
                    className="w-full py-1.5 px-3 text-xs font-bold text-slate-700 hover:text-indigo-600 hover:bg-white border border-slate-200 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Settings className="w-3.5 h-3.5" /> Manage Entity Registry
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {deleteModal}
      </div>
    );
  }

  // Sidebar Variant (Rich Entity Card with dropdown)
  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        id="btn-vendor-switcher-sidebar"
        className="p-3 bg-slate-50 hover:bg-indigo-50/50 border border-slate-200 hover:border-indigo-200 rounded-2xl transition-all cursor-pointer group shadow-xs"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-indigo-600/10 text-indigo-700 flex items-center justify-center font-bold shrink-0">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-black uppercase tracking-wider text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                  {activeLabel}
                </span>
                {profileCount > 1 && (
                  <span className="text-[9px] font-bold text-slate-400">
                    {profileCount} profiles
                  </span>
                )}
              </div>
              <p className="font-bold text-xs text-slate-900 truncate mt-0.5 group-hover:text-indigo-600 transition-colors">
                {activeName}
              </p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        {vendorProfile?.naicsCodes && vendorProfile.naicsCodes.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-500">
            <span className="font-medium truncate">{vendorProfile.naicsCodes.length} NAICS Tracked</span>
            <span className="font-mono text-[9px] text-slate-400">{vendorProfile.businessRegisteredState || 'All States'}</span>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl py-2 z-50 overflow-hidden"
          >
            <div className="px-4 py-2.5 border-b border-slate-100">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Your Vendor Entities</p>
              <p className="text-xs font-bold text-slate-800">{profileCount} Available {profileCount === 1 ? 'Profile' : 'Profiles'}</p>
            </div>

            <div className="max-h-72 overflow-y-auto py-1 divide-y divide-slate-50">
              {vendorProfiles.map((p) => {
                const isSelected = p.id === activeProfileId;
                return (
                  <div
                    key={p.id}
                    onClick={() => p.id && handleSelectProfile(p.id)}
                    className={`px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer transition-colors group/item ${
                      isSelected ? 'bg-indigo-50/70 border-l-4 border-indigo-600' : ''
                    }`}
                  >
                    <div className="min-w-0 pr-3">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-xs font-bold truncate ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                          {p.companyName || 'Untitled Entity'}
                        </p>
                        {p.isDefault && (
                          <span className="text-[8px] font-extrabold uppercase px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                            Default
                          </span>
                        )}
                        {isSelected && (
                          <span className="text-[8px] font-extrabold uppercase px-1.5 py-0.5 bg-indigo-600 text-white rounded">
                            Active
                          </span>
                        )}
                        {p.businessEntityData && (
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" title="Corporate Registry Verified" />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                        {p.label || 'Entity'} • {p.businessRegisteredState || 'US'} {p.ein ? `• EIN: ${p.ein}` : ''}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        title="Delete business entity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEntityToDelete(p);
                        }}
                        className="p-1.5 hover:bg-rose-100 text-slate-400 hover:text-rose-600 rounded-lg transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {isSelected ? (
                        <Check className="w-4 h-4 text-indigo-600" />
                      ) : (
                        <span className="text-[10px] font-bold text-indigo-600 opacity-0 group-hover/item:opacity-100">
                          Switch
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {onNavigateToProfiles && (
              <div className="p-2 border-t border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onNavigateToProfiles();
                  }}
                  id="btn-manage-all-profiles"
                  className="w-full py-2 px-3 text-xs font-bold text-slate-700 hover:text-indigo-600 hover:bg-white border border-slate-200 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Settings className="w-3.5 h-3.5" /> Manage Entity Registry
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {deleteModal}
    </div>
  );
};
